/**
 * Auth Provider Detection
 *
 * Determines which authentication provider to use based on localStorage cache.
 * During the Kinde migration, this module manages the dual-provider state.
 *
 * Flow:
 * 1. Check localStorage for cached provider preference
 * 2. Default to MSAL (B2C) if no cache exists
 * 3. After login, backend determines provider and cache is updated
 *
 * @module authProviderDetection
 */

/**
 * Supported authentication providers
 *
 * @readonly
 * @enum {string}
 */
export const AUTH_PROVIDERS = {
    /** Azure AD B2C (MSAL) - legacy provider */
    MSAL: 'B2C',
    /** Kinde - new provider */
    KINDE: 'Kinde',
}

const STORAGE_KEY = 'quotemate_auth_provider'

/**
 * Gets the current authentication provider from localStorage cache.
 * Returns null if no provider is cached - caller must use endpoint to detect.
 *
 * @returns {string|null} AUTH_PROVIDERS.MSAL, AUTH_PROVIDERS.KINDE, or null if not cached
 */
export const getAuthProvider = () => {
    try {
        const cached = localStorage.getItem(STORAGE_KEY)
        if (cached && Object.values(AUTH_PROVIDERS).includes(cached)) {
            console.debug('[AuthProvider] Using cached provider:', cached)
            return cached
        }

        console.debug('[AuthProvider] No cached provider')
    } catch {
        // localStorage not available (SSR or privacy mode)
        console.debug('[AuthProvider] localStorage not available')
    }

    return null
}

/**
 * Sets the authentication provider in localStorage cache.
 * Called after backend determines the correct provider for a user's email.
 *
 * @param {string} provider - AUTH_PROVIDERS value ('B2C' or 'Kinde')
 */
export const setAuthProvider = (provider) => {
    if (!Object.values(AUTH_PROVIDERS).includes(provider)) {
        console.warn('[AuthProvider] Invalid provider:', provider)
        return
    }

    try {
        localStorage.setItem(STORAGE_KEY, provider)
        console.debug('[AuthProvider] Provider set to:', provider)
    } catch {
        console.warn('[AuthProvider] Failed to cache provider to localStorage')
    }
}

/**
 * Clears the authentication provider cache.
 * Should be called on logout to ensure fresh provider detection on next login.
 */
export const clearAuthProvider = () => {
    try {
        localStorage.removeItem(STORAGE_KEY)
        console.debug('[AuthProvider] Provider cache cleared')
    } catch {
        console.warn('[AuthProvider] Failed to clear provider from localStorage')
    }
}

/**
 * Checks if the current user should use Kinde authentication.
 *
 * @returns {boolean} True if Kinde should be used
 */
export const isKindeUser = () => {
    return getAuthProvider() === AUTH_PROVIDERS.KINDE
}

/**
 * Checks if the current user should use MSAL (Azure AD B2C) authentication.
 *
 * @returns {boolean} True if MSAL should be used
 */
export const isMsalUser = () => {
    return getAuthProvider() === AUTH_PROVIDERS.MSAL
}

/**
 * Gets the authentication provider to use for new user registration.
 * Controlled by `VITE_REGISTRATION_PROVIDER` env var for staged rollout:
 * - Set to 'Kinde' to route new sign-ups through Kinde
 * - Set to 'B2C' (or omit) to keep using Azure AD B2C
 *
 * This is a transitional mechanism removed in Phase 6 (MSAL cleanup).
 *
 * @returns {string} AUTH_PROVIDERS.KINDE or AUTH_PROVIDERS.MSAL
 */
export const getRegistrationProvider = () => {
    const provider = import.meta.env.VITE_REGISTRATION_PROVIDER
    if (provider && Object.values(AUTH_PROVIDERS).includes(provider)) {
        return provider
    }
    return AUTH_PROVIDERS.MSAL
}

/**
 * Checks if the email-first login flow is enabled.
 * When disabled, unauthenticated users go directly to the default provider login
 * instead of the /login email entry page.
 *
 * Defaults to false (disabled). Set VITE_EMAIL_FIRST_LOGIN_ENABLED=true in .env
 * to activate the email-first provider detection flow during migration.
 *
 * @returns {boolean} True if email-first login flow is enabled
 */
export const isEmailFirstLoginEnabled = () => {
    return import.meta.env.VITE_EMAIL_FIRST_LOGIN_ENABLED === 'true'
}
